import { useStore } from '../store';
import {
  buildSitemapXml,
  defaultFileName,
  downloadBlob,
  pagesToCsv,
} from '../utils/export';
import { formatDateTime, formatDuration } from '../utils/format';
import { compareVersion, VIEWER_SUPPORTED_SCHEMA } from '../utils/version';

export function Header() {
  const data = useStore((s) => s.data);
  const fileName = useStore((s) => s.fileName);
  const clear = useStore((s) => s.clear);
  const openCommandBuilder = useStore((s) => s.openCommandBuilder);

  if (!data) return <></>;

  let host = '-';
  try {
    host = new URL(data.crawl.seedUrl).host;
  } catch {
    /* noop */
  }

  // スキーマバージョンの簡易チェック。
  // 古いバージョンの JSON が来た場合、Header に控えめな案内を出す（技術エラーは出さない）
  const schemaVersion = data.version ?? '不明';
  const isOlderSchema =
    schemaVersion !== '不明' &&
    compareVersion(schemaVersion, VIEWER_SUPPORTED_SCHEMA) < 0;

  // _meta.size から推定トークン数（読みやすい単位に変換）
  const tokens = data._meta?.ai_guidance?.size?.estimatedTokens;
  const tokensLabel = tokens !== undefined ? formatTokens(tokens) : null;

  const exportCsv = (): void => {
    if (!data) return;
    // url 付き warnings を CSV の警告列に展開する
    downloadBlob(pagesToCsv(data.pages, data.warnings), defaultFileName(data, 'csv'));
  };

  // トークン数を「12.3k」「1.2M」形式に整形（小さく表示するため簡潔に）
  function formatTokens(n: number): string {
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
    return `${(n / 1_000_000).toFixed(2)}M`;
  }
  const exportXml = (): void => {
    if (!data) return;
    downloadBlob(buildSitemapXml(data), 'sitemap.xml');
  };

  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-4">
        <div className="min-w-0">
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 shrink-0">{host}</h1>
            <span
              className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700"
              title="Sitemap Viewer は開発中です。仕様・見た目は予告なく変わります"
            >
              Beta
            </span>
            <span
              className="truncate text-xs text-slate-500 max-w-xs"
              title={fileName ?? undefined}
            >
              {fileName}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            <span className="font-medium">{data.crawl.stats.pagesCrawled}</span> ページ取得 ·{' '}
            <span className="font-medium">{data.crawl.stats.pagesErrored}</span> エラー ·{' '}
            <span className="font-medium">{data.warnings.length}</span> 警告 ·{' '}
            {formatDateTime(data.crawl.startedAt)} 開始 · 所要時間{' '}
            {formatDuration(data.crawl.startedAt, data.crawl.completedAt)}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400">
            <span>schema {schemaVersion}</span>
            {tokensLabel && (
              <span title="このファイルを AI に渡したときの推定トークン数">
                AI 推定 {tokensLabel} tokens
              </span>
            )}
            {isOlderSchema && (
              <span
                className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-amber-700"
                title={`Viewer は schema ${VIEWER_SUPPORTED_SCHEMA} 向けに作られています。一部の表示が空欄になる可能性があります。`}
              >
                ℹ 古いバージョンの JSON です
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openCommandBuilder}
            className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
          >
            使い方・コマンド生成
          </button>
          <button
            onClick={exportCsv}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
          >
            CSV ダウンロード
          </button>
          <button
            onClick={exportXml}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
          >
            sitemap.xml
          </button>
          <button
            onClick={clear}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-50"
          >
            別の JSON を読込
          </button>
        </div>
      </div>
    </header>
  );
}
